import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Eye,
  X,
  Zap,
  Upload,
} from "lucide-react";
import GlowCard from "../components/GlowCard";
import StepTracker, { TrackerStep } from "../components/StepTracker";
import ConfirmDialog from "../StandardsLibrary/ConfirmDialog";
import { VerityProject, reviewersOf } from "../Projects/types";
import PackageModal, { PackageFormMode } from "./PackageModal";
import StatusBadge from "../components/StatusBadge";
import PackageHierarchy from "./PackageHierarchy";
import PackageDocViewer from "./PackageDocViewer";
import PackageDocuments from "./PackageDocuments";
import AwpReviewEngine from "./AwpReviewEngine";
import AwpFindings from "./AwpFindings";
import { deletePackage, listPackages, setPackageCwps, updatePackage, uploadPackages } from "./api";
import {
  PACKAGE_TYPES,
  PackageDraft,
  PackageType,
  AwpRun,
  WorkArea,
  WorkPackage,
  cwpsLinking,
  documentStateOf,
  isPlaced,
} from "./types";

interface AwpWorkspaceProps {
  project: VerityProject;
  /** Admin shell: the register is shown read-only — an administrator follows a project, as on the
   *  engineering side, rather than doing its work. */
  admin?: boolean;
  onBack: () => void;
}

/**
 * The AWP project's stages, in the engineering project's shape: Deliverables → Document AI →
 * Compliance Engine → Findings becomes Packages → Documents → Review Engine → Findings.
 */
type AwpStepKey = "packages" | "documents" | "review" | "findings";
const AWP_STEPS: TrackerStep<AwpStepKey>[] = [
  { key: "packages", label: "Packages" },
  { key: "documents", label: "Documents" },
  { key: "review", label: "Review Engine" },
  { key: "findings", label: "Findings" },
];

/** Within Packages: the register as a table, or as the work-area hierarchy. */
type View = "register" | "hierarchy";

/**
 * Inside an AWP project: its work packages, as a register and as a hierarchy.
 *
 * This is the AWP counterpart of the engineering ProjectWorkspace. Where that one walks deliverables
 * through parse → check → findings, this one holds the packages the AWP rules will be applied to.
 * Documents per package and the review itself are the next steps and are not built yet.
 */
const AwpWorkspace = ({ project, admin = false, onBack }: AwpWorkspaceProps) => {
  const [packages, setPackages] = useState<WorkPackage[] | null>(null);
  // Read with the register rather than taken from the project row, which may predate the last edit.
  const [cwas, setCwas] = useState<WorkArea[]>(project.cwas ?? []);
  /** Which document reader the server is on — see documentStateOf. 0 until the register loads. */
  const [extractorVersion, setExtractorVersion] = useState(0);
  // The last cross-package review. It arrives on the project and is replaced in place by a run or
  // a decision, so the two stages that show it never disagree about what the current run is.
  const [run, setRun] = useState<AwpRun | null>(project.awpRun ?? null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<AwpStepKey>("packages");
  const [view, setView] = useState<View>("register");
  const [typeFilter, setTypeFilter] = useState<PackageType | "all">("all");
  const [dragging, setDragging] = useState(false);
  /** How many files are being read; 0 when idle. Drives the drop zone's busy state. */
  const [uploading, setUploading] = useState(0);
  const [uploadNote, setUploadNote] = useState<{ added: number; failed: { fileName: string; error: string }[] } | null>(
    null,
  );
  const fileInput = useRef<HTMLInputElement>(null);
  /** The package open in the form, and which half: details (pencil) or plan & link (lightning). */
  const [editing, setEditing] = useState<{ pkg: WorkPackage; mode: PackageFormMode } | null>(null);
  /** The package whose source document is open on its own, read-only. */
  const [viewing, setViewing] = useState<WorkPackage | null>(null);
  const [confirming, setConfirming] = useState<WorkPackage | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const result = await listPackages(project.id);
      setPackages(result.packages);
      setCwas(result.cwas);
      setExtractorVersion(result.extractorVersion);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the packages.");
      setPackages([]);
    }
  }, [project.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const all = useMemo(() => packages ?? [], [packages]);
  const byId = useMemo(() => new Map(all.map((p) => [p.id, p])), [all]);
  const cwaById = useMemo(() => new Map(cwas.map((a) => [a.id, a])), [cwas]);

  // A stage greens only once it has produced something: Packages when every package is placed in the
  // plan (work area + links) — a register with loose packages is not ready to review — and Documents
  // when every package's document has been read. The Review Engine compares packages against each
  // other, so one unread package is a gap in every comparison it takes part in.
  /** Packages not yet placed in the plan; an empty register counts as not ready too. */
  const unplaced = useMemo(
    () => (all.length === 0 ? 1 : all.filter((p) => !isPlaced(p, all)).length),
    [all],
  );
  /** Packages with no document, or whose document has not been read by the current reader. */
  const unread = useMemo(
    () =>
      all.length === 0 ? 1 : all.filter((p) => documentStateOf(p, extractorVersion) !== "read").length,
    [all, extractorVersion],
  );
  const doneSteps = useMemo<AwpStepKey[]>(() => {
    const done: AwpStepKey[] = [];
    if (unplaced === 0) done.push("packages");
    if (unread === 0) done.push("documents");
    // A run is what completes the Review Engine — which lights the Findings step, the way parsing
    // a deliverable lights the engineering flow's next step.
    if (run) done.push("review");
    return done;
  }, [unplaced, unread, run]);
  const nextStep = AWP_STEPS[AWP_STEPS.findIndex((s) => s.key === step) + 1];

  const counts = useMemo(
    () => ({
      CWP: all.filter((p) => p.type === "CWP").length,
      EWP: all.filter((p) => p.type === "EWP").length,
      PWP: all.filter((p) => p.type === "PWP").length,
    }),
    [all],
  );

  // CWPs first, then EWPs, then PWPs — the order the hierarchy reads in — and by number within each.
  const rows = useMemo(
    () =>
      all
        .filter((p) => typeFilter === "all" || p.type === typeFilter)
        .sort(
          (a, b) =>
            PACKAGE_TYPES.indexOf(a.type) - PACKAGE_TYPES.indexOf(b.type) ||
            a.packageNo.localeCompare(b.packageNo),
        ),
    [all, typeFilter],
  );

  /** What a package is linked to, as package numbers, for the register's "Linked" column. */
  const linksOf = (p: WorkPackage): { text: string; missing: boolean } => {
    const nos = (ids?: string[]) => (ids ?? []).map((id) => byId.get(id)?.packageNo).filter(Boolean);
    if (p.type === "CWP") {
      const parts = [...nos(p.ewpIds), ...nos(p.pwpIds)];
      return { text: parts.join(", ") || "No EWP / PWP", missing: parts.length === 0 };
    }
    const cwps = cwpsLinking(p, all).map((c) => c.packageNo);
    const feeds = p.type === "EWP" ? nos(p.feedsPwpIds) : [];
    const text = [cwps.join(", ") || "No CWP", feeds.length ? `feeds ${feeds.join(", ")}` : ""]
      .filter(Boolean)
      .join(" · ");
    return { text, missing: cwps.length === 0 };
  };

  /** Dropped or chosen files → one package each, added to the register as they come back. */
  const addFiles = async (files: File[]) => {
    if (files.length === 0 || uploading) return;
    setUploading(files.length);
    setUploadNote(null);
    setError(null);
    try {
      const { created, failed } = await uploadPackages(project.id, files);
      setPackages((prev) => [...(prev ?? []), ...created]);
      setUploadNote({ added: created.length, failed });
      setView("register");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the packages.");
    } finally {
      setUploading(0);
    }
  };

  const saveEdit = async (draft: PackageDraft, cwpIds?: string[]) => {
    if (!editing) return;
    const updated = await updatePackage(project.id, editing.pkg.id, draft);
    // An EWP or PWP linked or unlinked from its own side: the links live on the CWPs, so the CWPs
    // that changed come back and replace their rows — which is what moves the status.
    const changedCwps = cwpIds ? await setPackageCwps(project.id, editing.pkg.id, cwpIds) : [];
    const byId = new Map([updated, ...changedCwps].map((p) => [p.id, p]));
    setEditing(null);
    setPackages((prev) => (prev ?? []).map((p) => byId.get(p.id) ?? p));
  };

  const remove = async (pkg: WorkPackage) => {
    setBusyId(pkg.id);
    setError(null);
    try {
      await deletePackage(project.id, pkg.id);
      setPackages((prev) => (prev ?? []).filter((p) => p.id !== pkg.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not remove the package.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="vr-page vr-page-fixed">
      <div className="vr-page-head">
        <div>
          <button className="vr-rowbtn" onClick={onBack} style={{ marginBottom: ".75rem" }}>
            <ArrowLeft size={12} /> Projects
          </button>
          <h1 className="vr-h-page">{project.name}</h1>
          <p style={{ marginTop: ".25rem" }}>
            {project.projectRef ? `${project.projectRef} · ` : ""}
            {project.supplier}
            {project.location ? ` · ${project.location}` : ""}
            {reviewersOf(project).length > 0
              ? ` · ${reviewersOf(project).length === 1 ? "Reviewer" : "Reviewers"}: ${reviewersOf(project)
                  .map((r) => r.name)
                  .join(", ")}`
              : ""}
          </p>
        </div>
        <div className="vr-head-actions">
          <span className="vr-cell-code">
            {counts.CWP} CWP · {counts.EWP} EWP · {counts.PWP} PWP · {cwas.length} work{" "}
            {cwas.length === 1 ? "area" : "areas"}
          </span>
          {/* The way forward, as on an engineering project: lit once this stage has produced what
              the next needs, muted before that, never locked. */}
          {nextStep && (
            <button
              className={`vr-btn ${doneSteps.includes(step) ? "vr-btn-glow" : "vr-btn-outline"}`}
              onClick={() => setStep(nextStep.key)}
              title={`Go to ${nextStep.label}`}
            >
              {nextStep.label} <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>

      {/* The same stage tracker an engineering project has, with the AWP stages. */}
      <StepTracker
        steps={AWP_STEPS}
        active={step}
        onSelect={setStep}
        done={doneSteps}
        pending={["review", "findings"]}
      />

      {error && <div className="vr-form-error">{error}</div>}

      <div className="vr-scroll-region">
        {/* Packages arrive as their documents: drop a CWP, EWP or PWP document — or several — and
            each becomes a package in the register below, typed and titled from the file. The same
            drop zone the engineering Deliverables stage has. */}
        {step === "packages" && !admin && (
          <div
            className={`vr-drop ${dragging ? "vr-dragging" : ""}${uploading ? " vr-drop-busy" : ""}`}
            onClick={() => !uploading && fileInput.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void addFiles(Array.from(e.dataTransfer.files ?? []));
            }}
          >
            {uploading ? (
              <>
                <Loader2 size={26} className="vr-spin-inline vr-drop-icon" />
                <span className="vr-drop-title">
                  Reading {uploading} {uploading === 1 ? "document" : "documents"}…
                </span>
                <span className="vr-drop-sub">Each one becomes a package below.</span>
              </>
            ) : (
              <>
                <Upload size={26} className="vr-drop-icon" />
                <span className="vr-drop-title">
                  Drag and drop package documents, or click to browse
                </span>
                <span className="vr-drop-sub">
                  PDF, Word or Excel — one CWP, EWP or PWP per file. Drop several at once.
                </span>
              </>
            )}
            <input
              ref={fileInput}
              type="file"
              multiple
              accept=".pdf,.docx,.xlsx,.xlsm,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: "none" }}
              onChange={(e) => {
                void addFiles(Array.from(e.target.files ?? []));
                e.target.value = "";
              }}
            />
          </div>
        )}

        {step === "packages" && uploadNote && (
          <div className={`vr-awp-readout${uploadNote.failed.length ? " vr-awp-readout-warn" : ""}`}>
            <span>
              {uploadNote.added > 0 &&
                `${uploadNote.added} ${uploadNote.added === 1 ? "package" : "packages"} added. Fix anything misread with the pencil, then place each one with the lightning bolt. `}
              {uploadNote.failed.map((f) => `${f.fileName}: ${f.error}`).join(" · ")}
            </span>
          </div>
        )}

        {step === "packages" && (
          <div className="vr-chips">
            {(
              [
                { key: "register", label: "Register" },
                { key: "hierarchy", label: "Hierarchy" },
              ] as const
            ).map((v) => (
              <button
                key={v.key}
                type="button"
                className={`vr-chip-btn${view === v.key ? " vr-active" : ""}`}
                onClick={() => setView(v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}

        {step === "packages" && view === "hierarchy" && packages !== null && (
          <PackageHierarchy
            packages={all}
            cwas={cwas}
            // From the hierarchy a package is opened to be placed — that is what this view is about.
            onOpen={admin ? undefined : (pkg) => setEditing({ pkg, mode: "plan" })}
          />
        )}

        {/* Every later stage needs a placed register. Reaching one early is allowed — the tracker
            never locks — but it says what is missing and where to go, the way an engineering
            project's Document AI says "Nothing parsed yet" until Deliverables has been done. */}
        {/* Documents is NOT behind this gate. Reading a package's document needs the document and
            nothing else — not a work area, not a link — and the cross-package checks read the
            package numbers the documents themselves state. Holding the reading back until the plan
            is finished would stall the one stage that can be done the moment a file arrives. */}
        {step !== "packages" && step !== "documents" && packages !== null && unplaced > 0 && (
          <GlowCard hover={false}>
            <div className="vr-soon">
              <div className="vr-soon-icon">
                <Zap size={28} />
              </div>
              <h2 className="vr-h-sec">{all.length === 0 ? "No packages yet" : "Packages not linked yet"}</h2>
              <p className="vr-muted" style={{ maxWidth: 480 }}>
                {all.length === 0
                  ? "This project has no packages. Add them on the Packages step first."
                  : `${unplaced} of ${all.length} ${all.length === 1 ? "package is" : "packages are"} not linked. Go back to Packages and press the ⚡ on each one to place it in the plan — its work area and links.`}
              </p>
              <button className="vr-btn vr-btn-outline" onClick={() => setStep("packages")}>
                <ArrowLeft size={14} /> Back to Packages
              </button>
            </div>
          </GlowCard>
        )}

        {step === "documents" && packages !== null && (
          <PackageDocuments
            projectId={project.id}
            packages={all}
            extractorVersion={extractorVersion}
            admin={admin}
            // The register upstream holds the truth: a package that has just been read comes back
            // with its receipt on it, and the Packages step must show the same thing this one does.
            onPackageChanged={(updated) =>
              setPackages((prev) => (prev ?? []).map((p) => (p.id === updated.id ? updated : p)))
            }
          />
        )}
        {step === "review" && packages !== null && (
          <AwpReviewEngine
            projectId={project.id}
            packages={all}
            extractorVersion={extractorVersion}
            run={run}
            admin={admin}
            onRun={setRun}
            onGoToDocuments={() => setStep("documents")}
            onGoToFindings={() => setStep("findings")}
          />
        )}
        {step === "findings" && packages !== null && (
          <AwpFindings
            projectId={project.id}
            run={run}
            admin={admin}
            onRun={setRun}
            onGoToReview={() => setStep("review")}
          />
        )}

        {step === "packages" && view === "register" && (
          <GlowCard className="vr-table-card" hover={false}>
            <div className="vr-table-head">
              <div className="vr-chips">
                {(["all", ...PACKAGE_TYPES] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    className={`vr-chip-btn${typeFilter === t ? " vr-active" : ""}`}
                    onClick={() => setTypeFilter(t)}
                  >
                    {t === "all" ? `All (${all.length})` : `${t} (${counts[t]})`}
                  </button>
                ))}
              </div>
              <button className="vr-rowbtn" onClick={() => void load()}>
                <RefreshCw size={12} /> Refresh
              </button>
            </div>

            <div className="vr-table-scroll">
              <table className="vr-table">
                <thead>
                  <tr>
                    <th>Package</th>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Discipline</th>
                    <th>CWA</th>
                    <th>Linked</th>
                    <th>Rev</th>
                    <th>Need / ROS</th>
                    <th>Hours</th>
                    <th>Status</th>
                    {!admin && <th style={{ textAlign: "right" }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const busy = busyId === p.id;
                    const links = linksOf(p);
                    return (
                      <tr key={p.id}>
                        <td className="vr-cell-title">
                          <div>
                            <strong>{p.packageNo}</strong>
                            {p.source && <div className="vr-cell-code">{p.source.fileName}</div>}
                          </div>
                        </td>
                        <td>
                          <span className={`vr-pkg-type vr-pkg-${p.type.toLowerCase()}`}>{p.type}</span>
                        </td>
                        <td>
                          {p.title}
                          {p.longLead && <span className="vr-pkg-flag">Long lead</span>}
                          {p.procurementCategory && <div className="vr-cell-code">{p.procurementCategory}</div>}
                        </td>
                        <td className="vr-cell-dim">{p.discipline || "—"}</td>
                        <td className="vr-cell-dim">{(p.cwaId && cwaById.get(p.cwaId)?.code) || "—"}</td>
                        <td className={links.missing ? "vr-warn" : "vr-cell-dim"}>{links.text}</td>
                        <td className="vr-cell-dim">{p.revision || "—"}</td>
                        <td className="vr-cell-dim">{(p.type === "PWP" ? p.rosDate : p.needDate) || "—"}</td>
                        <td className="vr-cell-dim">{p.hours ?? "—"}</td>
                        {/* Derived from the plan — work area and links — never set by hand. */}
                        <td>
                          {isPlaced(p, all) ? (
                            <StatusBadge status="pass" label="Linked" />
                          ) : (
                            <StatusBadge status="warning" label="Not linked" />
                          )}
                        </td>
                        {!admin && (
                          <td>
                            <div className="vr-cell-actions">
                              {/* Read the document the package was made from, without opening a form. */}
                              {p.source && (
                                <button
                                  className="vr-rowbtn vr-icononly"
                                  onClick={() => setViewing(p)}
                                  title={`View ${p.source.fileName}`}
                                  aria-label="View document"
                                >
                                  <Eye size={13} />
                                </button>
                              )}
                              {/* Pencil: correct what was read from the document. Status unchanged. */}
                              <button
                                className="vr-rowbtn vr-icononly vr-edit-amber"
                                onClick={() => setEditing({ pkg: p, mode: "details" })}
                                disabled={busy}
                                title="Edit details — type, number, title, discipline, revision"
                                aria-label="Edit package details"
                              >
                                <Pencil size={13} />
                              </button>
                              {/* Lightning: place it in the plan — work area, links, dates. */}
                              {/* Green once the package is placed — the same done-mark as an engineering
                                  deliverable's parse bolt. */}
                              <button
                                className={`vr-rowbtn vr-icononly${isPlaced(p, all) ? " vr-parsed" : ""}`}
                                onClick={() => setEditing({ pkg: p, mode: "plan" })}
                                disabled={busy}
                                title={
                                  isPlaced(p, all)
                                    ? "Linked — open to change its work area, links, dates and hours"
                                    : "Plan & link — work area, linked packages, dates and hours"
                                }
                                aria-label="Plan and link package"
                              >
                                <Zap size={13} />
                              </button>
                              <button
                                className="vr-rowbtn vr-icononly vr-danger"
                                onClick={() => setConfirming(p)}
                                disabled={busy}
                                title="Remove this package"
                                aria-label="Remove package"
                              >
                                {busy ? <Loader2 size={13} className="vr-spin-inline" /> : <Trash2 size={13} />}
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {packages === null && (
              <div className="vr-empty">
                <Loader2 size={16} className="vr-spin-inline" /> Loading packages…
              </div>
            )}
            {packages !== null && rows.length === 0 && (
              <div className="vr-empty">
                {all.length === 0
                  ? admin
                    ? "No packages entered yet."
                    : "No packages yet. Drop the package documents above — one CWP, EWP or PWP per file."
                  : "No packages of this type."}
              </div>
            )}
          </GlowCard>
        )}
      </div>

      {editing && (
        <PackageModal
          key={`${editing.pkg.id}:${editing.mode}`}
          pkg={editing.pkg}
          mode={editing.mode}
          projectId={project.id}
          register={all}
          cwas={cwas}
          onCancel={() => setEditing(null)}
          onSubmit={saveEdit}
        />
      )}

      {viewing && (
        <div className="vr-backdrop" role="dialog" aria-modal="true" aria-label="Package document">
          <div className="vr-modal vr-modal-wide">
            <div className="vr-modal-head">
              <div>
                <h3>
                  {viewing.type} {viewing.packageNo}
                </h3>
                <p>{viewing.title}</p>
              </div>
              <button className="vr-rowbtn vr-icononly" onClick={() => setViewing(null)} aria-label="Close">
                <X size={14} />
              </button>
            </div>
            <div className="vr-modal-body">
              <PackageDocViewer projectId={project.id} pkg={viewing} />
            </div>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Remove this package?"
          tone="danger"
          confirmLabel="Remove"
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            const target = confirming;
            setConfirming(null);
            void remove(target);
          }}
        >
          <p className="vr-confirm-lead">
            <strong>{confirming.packageNo}</strong> — {confirming.title}
          </p>
          <p className="vr-confirm-stress">This cannot be undone.</p>
        </ConfirmDialog>
      )}
    </div>
  );
};

export default AwpWorkspace;
